import { useState } from "react";
import { toast } from "sonner";
import { Users, RefreshCw, Trash2, X, UserPlus, UserMinus } from "lucide-react";
import { api } from "@/lib/api";
import { useAzure, azErr } from "@/context/AzureContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

export default function EntraGroups() {
  const { state, run, refresh } = useAzure();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [addUser, setAddUser] = useState("");
  const [form, setForm] = useState({ name: "", group_type: "Security", membership_type: "Assigned", description: "" });

  const groups = state?.entra_groups || [];
  const users = state?.entra_users || [];
  const userName = (oid) => users.find((u) => u.object_id === oid)?.display_name || oid;

  const submit = () =>
    run(() => api.post("/azure/entra/groups", form), { success: `Created group ${form.name}` }).then((r) => {
      if (r.ok) setOpen(false);
    });

  const del = (g) => {
    if (!window.confirm(`Delete group ${g.name}?`)) return;
    run(() => api.delete(`/azure/entra/groups/${g.object_id}`), { success: "Group deleted" }).then(() => setSelected(null));
  };

  const member = async (add, userId) => {
    const g = selected;
    const res = await run(() => api.post(`/azure/entra/groups/${g.object_id}/members`, { user_id: userId, add }),
      { success: add ? "Member added" : "Member removed" });
    if (res.ok) {
      const fresh = await api.get("/azure/state");
      setSelected(fresh.data.entra_groups.find((x) => x.object_id === g.object_id));
      setAddUser("");
    }
  };

  const nonMembers = selected ? users.filter((u) => !selected.members.includes(u.object_id)) : [];

  return (
    <div data-testid="az-entra-groups">
      <div className="flex items-center gap-2 border-b border-border pb-3 mb-3">
        <Button size="sm" onClick={() => { setForm({ name: "", group_type: "Security", membership_type: "Assigned", description: "" }); setOpen(true); }} data-testid="az-new-group-btn" className="bg-[#0078d4] hover:bg-[#106ebe] text-white gap-2">
          <Users className="h-4 w-4" /> New group
        </Button>
        <Button size="sm" variant="ghost" onClick={refresh} className="gap-2"><RefreshCw className="h-4 w-4" /> Refresh</Button>
      </div>

      <div className="border border-border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Membership</TableHead><TableHead>Members</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {groups.map((g) => (
              <TableRow key={g.object_id} className="cursor-pointer" data-testid={`az-group-row-${g.name}`} onClick={() => setSelected(g)}>
                <TableCell className="font-medium text-[#0078d4]">{g.name}</TableCell>
                <TableCell>{g.group_type}</TableCell>
                <TableCell>{g.membership_type}</TableCell>
                <TableCell>{g.members.length}</TableCell>
              </TableRow>
            ))}
            {groups.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No groups.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New group</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Group name *</Label><Input data-testid="az-group-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Finance-Users" /></div>
            <div className="space-y-1.5">
              <Label>Group type</Label>
              <Select value={form.group_type} onValueChange={(v) => setForm({ ...form, group_type: v })}>
                <SelectTrigger data-testid="az-group-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Security">Security</SelectItem><SelectItem value="Microsoft 365">Microsoft 365</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="az-group-create-submit" onClick={submit} className="bg-[#0078d4] hover:bg-[#106ebe] text-white">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {selected && (
        <div className="fixed inset-y-0 right-0 w-full max-w-md bg-card border-l border-border shadow-2xl z-50 flex flex-col" data-testid="az-group-detail">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div><div className="font-display font-bold text-lg">{selected.name}</div><div className="text-xs text-muted-foreground">{selected.group_type} · {selected.membership_type}</div></div>
            <Button size="icon" variant="ghost" onClick={() => setSelected(null)}><X className="h-4 w-4" /></Button>
          </div>
          <div className="px-5 py-3 border-b border-border flex gap-2">
            <Button size="sm" variant="outline" className="gap-2 text-red-600" onClick={() => del(selected)}><Trash2 className="h-4 w-4" /> Delete group</Button>
          </div>
          <div className="p-5 space-y-4 overflow-y-auto">
            <div className="text-xs text-muted-foreground break-all">Object ID: {selected.object_id}</div>
            <div>
              <div className="text-sm font-semibold mb-2">Add member</div>
              <div className="flex gap-2">
                <Select value={addUser} onValueChange={setAddUser}>
                  <SelectTrigger data-testid="az-group-add-select" className="flex-1"><SelectValue placeholder="Select a user" /></SelectTrigger>
                  <SelectContent>{nonMembers.map((u) => <SelectItem key={u.object_id} value={u.object_id}>{u.display_name}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" className="gap-1.5 bg-[#0078d4] hover:bg-[#106ebe] text-white" data-testid="az-group-add-btn" disabled={!addUser} onClick={() => member(true, addUser)}><UserPlus className="h-4 w-4" /> Add</Button>
              </div>
            </div>
            <div>
              <div className="text-sm font-semibold mb-2">Members ({selected.members.length})</div>
              <div className="space-y-1">
                {selected.members.map((oid) => (
                  <div key={oid} className="flex items-center justify-between border border-border rounded-md px-3 py-2 text-sm">
                    <span>{userName(oid)}</span>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => member(false, oid)}><UserMinus className="h-4 w-4" /></Button>
                  </div>
                ))}
                {selected.members.length === 0 && <div className="text-muted-foreground text-sm">No members yet.</div>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
